import React, { useState, useEffect } from 'react';
import { Flex, Divider, Button, message, Modal, Result } from 'antd';
import axios from 'axios';
import caricon from '../../../images/car-icon.svg';
import useLineLiff from 'hooks/LineLiff';
import Header from 'pages/booking/components/Header';
import { decodeBase64Object, encodeBase64Object } from 'shared/common';
import toyotaLogo from '../../../assets/TOYOTA LOGO - Horizontal.svg';
import { Link } from 'react-router-dom';

import '../../booking/booking.scss';

const LoginRequiredResult = ({ apiResponse }) => {
  const [countdown, setCountdown] = useState(5);
  const redirectUrl = apiResponse?.data?.redirect_url;

  useEffect(() => {
    if (!redirectUrl) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = redirectUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [redirectUrl]);

  const handleLoginNow = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  return (
    <div className="booking-container">
      <div className="toyota-header" style={{ height: '60px' }}>
        <Link to="/">
          <img className="toyota-logo" src={toyotaLogo} alt="TOYOTA LOGO" />
        </Link>
      </div>
      <div className="form-container">
        <div className="form-section">
          <Result
            status="warning"
            title="需要重新登入"
            subTitle={
              <div>
                <div style={{ marginBottom: '12px' }}>
                  您的登入狀態已過期，請重新登入以繼續操作
                </div>
                {countdown > 0 && (
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {countdown} 秒後自動跳轉到登入頁面...
                  </div>
                )}
              </div>
            }
            extra={[
              <Button
                type="primary"
                key="login"
                onClick={handleLoginNow}
                style={{
                  height: '40px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  marginRight: '8px',
                }}>
                立即重新登入
              </Button>,
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const TestDrive = () => {
  const { liff, userInfo } = useLineLiff(process.env.REACT_APP_LIFF_ID_PNP);
  const [pageLoading, setPageLoading] = useState(true);
  const [pnpData, setPnpData] = useState({});
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [loginRequired, setLoginRequired] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);

  useEffect(() => {
    if (!userInfo) return;

    const idToken = liff.getIDToken();

    const queryParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );
    // decode base64
    const decodedParams = decodeBase64Object(queryParams);
    setPnpData(decodedParams);

    document.title = `TOYOTA | 預約提醒通知`;

    setPageLoading(false);
  }, [liff, userInfo]);

  const handleAddToCalendar = async () => {
    const eventConfig = {
      title: 'TOYOTA 試乘預約',
      description: [
        pnpData.description || '',
        `試乘車款: ${pnpData.content || ''}`,
        `試乘據點: ${pnpData.location || ''}`,
      ]
        .filter(Boolean)
        .join('\n'),
      location: pnpData.location || '',
      startDate: pnpData.appointed_date,
      startTime: pnpData.appointed_time,
      filename: 'toyota-testdrive.ics',
      categories: ['TOYOTA', 'TestDrive', 'Appointment'],
    };

    const buildQueryParams = new URLSearchParams(
      encodeBase64Object({
        title: eventConfig.title,
        description: eventConfig.description,
        location: eventConfig.location,
        startDate: eventConfig.startDate,
        startTime: eventConfig.startTime,
        filename: eventConfig.filename,
        categories: eventConfig.categories.join(','),
      })
    ).toString();

    const fullUrl = `${window.location.origin}/hotai/toyota/pnp/downloadCalendar?${buildQueryParams}`;

    liff.openWindow({
      url: fullUrl,
      external: true,
    });
  };

  const handleShowLocation = () => {
    // Show location functionality
    console.log('Showing location...');
  };

  const handleCancelRegistration = () => {
    Modal.confirm({
      title: '確認取消預約',
      content: '您確定要取消這次的試乘預約嗎？此動作無法復原。',
      okText: '確認取消',
      cancelText: '返回',
      okType: 'danger',
      onOk: async () => {
        setIsCancelling(true);

        try {
          const idToken = liff.getIDToken();
          const pnpId = pnpData.id || pnpData.pnp_id;

          if (!pnpId) {
            throw new Error('預約ID不存在');
          }

          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/pnp/${pnpId}/cancel_booking`,
            {
              line_id_token: idToken,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
              timeout: 30000,
            }
          );

          const responseData = response.data;
          setApiResponse(responseData);

          if (response.status === 200 && responseData.success === true) {
            setIsCancelled(true);
            message.success('預約已成功取消');
          } else if (
            response.status === 200 &&
            responseData.success === false
          ) {
            // Handle failed response
            if (responseData.error === 'one_id_refresh_user_failed') {
              // Use the same approach as BookingCancel.js
              setLoginRequired(true);
            } else {
              message.error(responseData.message || '取消預約失敗，請稍後再試');
            }
          } else {
            throw new Error('未預期的回應狀態');
          }
        } catch (error) {
          console.error('Cancel booking error:', error);
          if (error.code === 'ECONNABORTED') {
            message.error('請求超時，請檢查網路連線後再試');
          } else if (error.response) {
            message.error(
              `取消預約失敗: ${error.response.data?.message || error.message}`
            );
          } else {
            message.error('取消預約失敗，請稍後再試');
          }
        } finally {
          setIsCancelling(false);
        }
      },
    });
  };

  const renderField = (label, value, showDivider = false) => (
    <>
      <div className="form-content">
        <Flex align="center">
          <span
            className="form-subtitle"
            style={{ width: '140px', minWidth: '140px' }}>
            {label}:
          </span>
          <span
            className="form-subtitle"
            style={{ fontWeight: 'bold', textAlign: 'left' }}>
            {value || '-'}
          </span>
        </Flex>
      </div>
      {showDivider && <Divider style={{ margin: '12px 0' }} />}
    </>
  );

  if (loginRequired) {
    return <LoginRequiredResult apiResponse={apiResponse} />;
  }

  if (isCancelled) {
    return (
      <div className="booking-container">
        <div className="toyota-header" style={{ height: '60px' }}>
          <Link to="/">
            <img className="toyota-logo" src={toyotaLogo} alt="TOYOTA LOGO" />
          </Link>
        </div>
        <div className="form-container">
          <div className="form-section">
            <Result
              status="success"
              title="預約已取消"
              subTitle={
                <div>
                  <div>您的試乘預約已成功取消</div>
                  <div>現在可以安心關閉此頁面</div>
                </div>
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-container">
      <div className="toyota-header" style={{ height: '60px' }}>
        <Link to="/">
          <img className="toyota-logo" src={toyotaLogo} alt="TOYOTA LOGO" />
        </Link>
      </div>
      <div className="form-container">
        <div className="form-section">
          <div
            className="form-content"
            style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Flex align="center" gap="small">
              <img src={caricon} alt="Car icon" />
              <div className="form-title" style={{ fontSize: '20px' }}>TOYOTA 預約提醒通知</div>
            </Flex>
          </div>

          <div className="form-content">
            <div className="form-subtitle">
              <span>{pnpData.description}</span>
            </div>
          </div>

          {renderField('試乘車款', pnpData.content)}
          {renderField('試乘日期', pnpData.appointed_date)}
          {renderField('試乘時間', pnpData.appointed_time)}
          {renderField('試乘據點', pnpData.location)}
        </div>

        <div
          style={{
            marginTop: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
          <Button
            type="primary"
            size="large"
            onClick={handleAddToCalendar}
            loading={pageLoading}
            style={{
              height: '50px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: '#52c41a',
              borderColor: '#52c41a',
              borderRadius: '8px',
            }}>
            加入行事曆
          </Button>

          <Button
            type="primary"
            size="large"
            onClick={() => {
              window.location.href = 'https://www.toyota.com.tw/location.aspx';
            }}
            loading={pageLoading}
            style={{
              height: '50px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: '#52c41a',
              borderColor: '#52c41a',
              borderRadius: '8px',
            }}
          >
            據點位置
          </Button>

          {/* <Button
            type="default"
            size="large"
            onClick={handleShowLocation}
            style={{
              height: '50px',
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '8px',
            }}>
            據點位置
          </Button> */}

          <Button
            type="default"
            size="large"
            onClick={handleCancelRegistration}
            loading={isCancelling}
            style={{
              height: '50px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#ffffff',
              borderColor: '#ff4d4f',
              borderRadius: '8px',
            }}
            onMouseEnter={(e) => (e.target.style.color = '#ff4d4f')} // Hover color
            onMouseLeave={(e) => (e.target.style.color = '#ffffff')} // Default color
          >
            取消預約
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TestDrive;
